<template>
  <div class="callback-page">
    <div class="callback-page__container">
      <!-- Loading State -->
      <div v-if="state === 'loading'" class="callback-page__content">
        <div class="callback-page__spinner"></div>
        <h1 class="callback-page__title">Processing your verification...</h1>
        <p class="callback-page__message">Please wait while we verify your identity.</p>
      </div>

      <!-- Success State (authenticated — PC flow) -->
      <div v-else-if="state === 'success' && isAuthenticated" class="callback-page__content">
        <div class="callback-page__icon callback-page__icon--success">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </div>
        <h1 class="callback-page__title">Verification Successful!</h1>
        <p class="callback-page__message">Your identity has been verified successfully.</p>
        <p class="callback-page__redirect">Redirecting to your account in {{ countdown }} seconds...</p>
      </div>

      <!-- Success State (unauthenticated — mobile QR flow) -->
      <div v-else-if="state === 'success' && !isAuthenticated" class="callback-page__content">
        <div class="callback-page__icon callback-page__icon--success">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </div>
        <h1 class="callback-page__title">Verification Complete!</h1>
        <p class="callback-page__message">Your identity has been verified successfully.</p>
        <p class="callback-page__message">You can close this browser tab and return to your computer.</p>
      </div>

      <!-- Error State -->
      <div v-else-if="state === 'error'" class="callback-page__content">
        <div class="callback-page__icon callback-page__icon--error">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h1 class="callback-page__title">Verification Failed</h1>
        <p class="callback-page__message">{{ errorMessage }}</p>
        <div class="callback-page__actions">
          <button @click="retryVerification" class="btn btn--primary">
            Try Again
          </button>
          <button @click="goToDashboard" class="btn btn--secondary">
            Go to My Account
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../../api';

export default {
  name: 'OneClickCallbackPage',
  data() {
    return {
      state: 'loading', // loading, success, error
      errorMessage: '',
      countdown: 3,
      countdownInterval: null,
      isAuthenticated: false
    };
  },
  mounted() {
    console.log('OneClickCallbackPage mounted');
    console.log('Query params:', this.$route.query);
    this.processCallback();
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  methods: {
    async processCallback() {
      console.log('processCallback called');
      try {
        // Extract JWT from URL query parameter
        const token = this.$route.query.token;
        console.log('Token extracted:', token ? 'Token exists' : 'No token');

        if (!token) {
          throw new Error('No verification token found in the URL. Please try starting the verification process again.');
        }

        console.log('Calling verifyIdentityCallback API...');
        // Call the callback API with the JWT token
        const response = await api.bangeo.verifyIdentityCallback(token);

        if (!response.data.verified) {
          throw new Error('Verification failed.');
        }

        console.log('Verification callback response:', response);

        // Detect whether this is the PC flow (authenticated) or the mobile QR
        // flow (no session). Mobile lands here via QR code without a KeeperFA
        // session, so we must not attempt RETRIEVE_USER_DATA — the CRM call
        // would 401 and incorrectly show an error despite verification succeeding.
        this.isAuthenticated = !!this.$store.state.user.user;

        // Show success immediately — before any async follow-up work so that
        // a failure in RETRIEVE_USER_DATA does not mask a successful verification.
        this.state = 'success';

        if (this.isAuthenticated) {
          // PC flow: refresh Vuex/CRM user data and auto-redirect to account page.
          await this.$store.dispatch('user/RETRIEVE_USER_DATA');
          this.startCountdown();
        }
        // Mobile QR flow: no session — template shows "close this tab" message.
        // The PC browser will see the updated verification status on next load.

      } catch (error) {
        console.error('Verification callback error:', error);

        // Show error state with appropriate message
        this.state = 'error';

        if (error.response) {
          // API returned an error response
          const status = error.response.status;
          const data = error.response.data;

          if (status === 400) {
            this.errorMessage = 'Invalid verification token. Please try starting the verification process again.';
          } else if (status === 401) {
            this.errorMessage = 'You are not authorized. Please log in and try again.';
          } else if (status === 500) {
            this.errorMessage = data.message || 'A server error occurred. Please try again later or contact support.';
          } else {
            this.errorMessage = data.message || 'An unexpected error occurred. Please try again.';
          }
        } else if (error.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = 'Unable to complete verification. Please check your internet connection and try again.';
        }
      }
    },
    startCountdown() {
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.goToDashboard();
        }
      }, 1000);
    },
    retryVerification() {
      // Reset state and try again
      this.state = 'loading';
      this.errorMessage = '';
      this.processCallback();
    },
    goToDashboard() {
      this.$router.push({ name: 'myAccount' });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~sass/_variables.scss';
@import "~sass/_config.scss";
@import "~sass/mixins/_media-query.scss";

.callback-page {
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.callback-page__container {
  max-width: 600px;
  width: 100%;
  background: $col-neutral--0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 60px 40px;

  @include maxq(tablet) {
    padding: 40px 30px;
  }

  @include maxq(mobile) {
    padding: 30px 20px;
  }
}

.callback-page__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.callback-page__spinner {
  width: 60px;
  height: 60px;
  border: 4px solid $col-neutral--3;
  border-top: 4px solid $col-t--1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 30px;

  @include maxq(tablet) {
    width: 50px;
    height: 50px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.callback-page__icon {
  margin-bottom: 30px;
  animation: scaleIn 0.5s ease-out;

  &--success {
    color: #61a700;
  }

  &--error {
    color: #d32f2f;
  }

  svg {
    width: 80px;
    height: 80px;

    @include maxq(tablet) {
      width: 60px;
      height: 60px;
    }
  }
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.callback-page__title {
  font-size: 28px;
  font-weight: 600;
  color: $col-neutral--9;
  margin: 0 0 15px 0;
  line-height: 1.3;

  @include maxq(tablet) {
    font-size: 24px;
  }

  @include maxq(mobile) {
    font-size: 20px;
  }
}

.callback-page__message {
  font-size: 16px;
  color: $col-neutral--7;
  margin: 0 0 15px 0;
  line-height: 1.5;

  @include maxq(tablet) {
    font-size: 15px;
  }

  @include maxq(mobile) {
    font-size: 14px;
  }
}

.callback-page__redirect {
  font-size: 14px;
  color: $col-neutral--6;
  margin: 10px 0 0 0;
  font-style: italic;

  @include maxq(mobile) {
    font-size: 13px;
  }
}

.callback-page__actions {
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;
  justify-content: center;

  @include maxq(mobile) {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }

  .btn {
    padding: 12px 30px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;

    @include maxq(mobile) {
      width: 100%;
    }

    &--primary {
      background-color: $col-t--1;
      color: $col-neutral--0;

      &:hover {
        background-color: darken($col-t--1, 10%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }

    &--secondary {
      background-color: $col-neutral--3;
      color: $col-neutral--8;

      &:hover {
        background-color: $col-neutral--4;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    }
  }
}
</style>
